import React, { useState } from "react";
import { toast } from "sonner";
import api, { downloadAuthed } from "@/lib/api";
import { Gift, Plus, Trash2, Send, CheckCircle2, Clock, FileSignature, Banknote, IdCard, Loader2, RefreshCw, FileText, Check, X } from "lucide-react";

const REL_OPTIONS = ["Parent", "Grandparent", "Sibling", "Child", "Spouse / Partner", "Other relative", "Friend", "Other"];

function fmtGBP(n) {
  try { return `£${Number(n).toLocaleString("en-GB", { minimumFractionDigits: 0 })}`; } catch { return `£${n}`; }
}
const GIFTOR_STEP_LABELS = { declaration: "Declaration", funds: "Proof of funds", id_check: "ID / AML" };
function _ovVerb(status) {
  return status === "accepted" ? "marked done" : status === "rejected" ? "cancelled & re-requested" : status;
}
function _ovDate(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short" }); }
  catch { return ""; }
}

function StepPill({ ok, label, Icon }) {
  return (
    <span className={`inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full border ${ok ? "bg-emerald-50 border-emerald-200 text-emerald-700" : "bg-slate-50 border-slate-200 text-slate-500"}`}>
      {ok ? <CheckCircle2 className="w-3 h-3" /> : <Icon className="w-3 h-3" />} {label}
    </span>
  );
}

// Shared add/list UI for giftors. `role` = "client" (buyer wizard) or
// "solicitor" (onboarding panel). Solicitors get download + remove controls.
export default function GiftorsManager({ caseId, role = "client", giftors = [], refresh }) {
  const [adding, setAdding] = useState(false);
  const [busy, setBusy] = useState(false);
  const [idBusy, setIdBusy] = useState("");
  const [form, setForm] = useState({ first_name: "", middle_name: "", surname: "", email: "", relationship: "", gift_amount: "" });
  const isSolicitor = role === "solicitor";

  const refreshId = async (g) => {
    const sid = g.didit?.session_id;
    if (!sid) { toast.error("This giftor hasn't started their ID check yet."); return; }
    setIdBusy(`refresh-${g.id}`);
    const tid = toast.loading(`Refreshing ${g.name}'s ID check…`);
    try {
      await api.post(`/didit/sessions/${sid}/refresh`);
      toast.success(`${g.name}'s ID status updated.`, { id: tid });
      await refresh?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not refresh the ID check.", { id: tid });
    } finally { setIdBusy(""); }
  };

  const idReport = async (g) => {
    const existing = g.didit?.report_doc_id;
    if (existing) {
      downloadAuthed(`/documents/${existing}/download`, `ID Report - ${g.name}.pdf`).catch(() => toast.error("Could not download the report."));
      return;
    }
    const sid = g.didit?.session_id;
    if (!sid) { toast.error("This giftor hasn't started their ID check yet."); return; }
    setIdBusy(`report-${g.id}`);
    const tid = toast.loading("Fetching ID report from Didit…");
    try {
      const { data } = await api.post(`/didit/sessions/${sid}/report`);
      toast.success("ID report attached to the case.", { id: tid });
      if (data?.report_doc_id) {
        await downloadAuthed(`/documents/${data.report_doc_id}/download`, `ID Report - ${g.name}.pdf`).catch(() => {});
      }
      await refresh?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't fetch the ID report.", { id: tid });
    } finally { setIdBusy(""); }
  };

  const decideId = async (g, decision) => {
    const sid = g.didit?.session_id;
    if (!sid) { toast.error("This giftor hasn't started their ID check yet."); return; }
    setIdBusy(`decide-${g.id}`);
    const tid = toast.loading(decision === "Approved" ? `Approving ${g.name}'s ID with Didit…` : `Declining ${g.name}'s ID…`);
    try {
      await api.post(`/didit/sessions/${sid}/approve-check`, { decision });
      toast.success(decision === "Approved" ? "ID check approved — Didit updated" : "ID check declined", { id: tid });
      await refresh?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update the ID check.", { id: tid });
    } finally { setIdBusy(""); }
  };

  const markStep = async (g, step, done) => {
    setIdBusy(`mark-${step}-${g.id}`);
    const tid = toast.loading(done ? "Marking step as done…" : "Removing manual mark…");
    try {
      await api.post(`/onboarding/cases/${caseId}/giftors/${g.id}/mark-step`, { step, done });
      toast.success(done ? "Step marked as done." : "Manual mark removed.", { id: tid });
      await refresh?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update the step.", { id: tid });
    } finally { setIdBusy(""); }
  };

  const rejectStep = async (g, step) => {
    setIdBusy(`reject-${step}-${g.id}`);
    const tid = toast.loading("Cancelling & re-requesting…");
    try {
      await api.post(`/onboarding/cases/${caseId}/giftors/${g.id}/mark-step`, { step, action: "reject" });
      toast.success(`Re-requested — ${g.name} has been sent their link again.`, { id: tid });
      await refresh?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not re-request the item.", { id: tid });
    } finally { setIdBusy(""); }
  };

  const submit = async () => {
    if (!form.first_name.trim() || !form.surname.trim() || !form.email.trim() || !form.relationship || !form.gift_amount) {
      toast.error("Please complete the giftor's first name, surname, email, relationship and gift amount.");
      return;
    }
    setBusy(true);
    try {
      const fullName = [form.first_name, form.middle_name, form.surname].map((x) => x.trim()).filter(Boolean).join(" ");
      await api.post(`/onboarding/cases/${caseId}/giftors`, {
        first_name: form.first_name.trim(), middle_name: form.middle_name.trim(), surname: form.surname.trim(),
        name: fullName, email: form.email.trim(), relationship: form.relationship,
        gift_amount: Number(form.gift_amount),
      });
      toast.success(`${fullName} has been emailed a secure link to complete their giftor checks.`);
      setForm({ first_name: "", middle_name: "", surname: "", email: "", relationship: "", gift_amount: "" });
      setAdding(false);
      refresh?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not add the giftor."); }
    finally { setBusy(false); }
  };

  const resend = async (g) => {
    try { await api.post(`/onboarding/cases/${caseId}/giftors/${g.id}/resend`); toast.success(`Re-sent the secure link to ${g.name}.`); }
    catch { toast.error("Could not resend the invite."); }
  };
  const remove = async (g) => {
    if (!window.confirm(`Remove ${g.name} as a giftor? Their link will stop working.`)) return;
    try { await api.delete(`/onboarding/cases/${caseId}/giftors/${g.id}`); toast.success("Giftor removed."); refresh?.(); }
    catch { toast.error("Could not remove the giftor."); }
  };

  return (
    <div data-testid="giftors-manager">
      <div className="space-y-3">
        {giftors.length === 0 && (
          <p className="text-sm text-slate-500" data-testid="giftors-empty">No giftors added yet. If someone is gifting money towards the deposit, add them below and we'll send them a secure link.</p>
        )}
        {giftors.map((g) => (
          <div key={g.id} className="rounded-lg border border-slate-200 bg-white p-3" data-testid={`giftor-row-${g.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <Gift className="w-4 h-4 text-[#c87d3a] shrink-0" />
                  <span className="font-medium text-slate-900 text-sm truncate">{g.name}</span>
                  <span className="text-[11px] text-slate-500">{g.relationship} · {fmtGBP(g.gift_amount)}</span>
                  <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${g.status === "complete" ? "bg-emerald-50 text-emerald-700" : g.status === "in_progress" ? "bg-amber-50 text-amber-700" : "bg-slate-100 text-slate-500"}`} data-testid={`giftor-status-${g.id}`}>
                    {g.status === "complete" ? "Complete" : g.status === "in_progress" ? "In progress" : "Awaiting giftor"}
                  </span>
                </div>
                <p className="text-[11px] text-slate-400 mt-0.5 truncate">{g.email}</p>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  <StepPill ok={g.steps?.declaration} label="Declaration" Icon={FileSignature} />
                  <StepPill ok={g.steps?.funds} label="Proof of funds" Icon={Banknote} />
                  <StepPill ok={g.steps?.id_check} label="ID / AML" Icon={IdCard} />
                </div>
                {isSolicitor && (
                  <div className="mt-2 flex flex-wrap gap-2">
                    {g.declaration?.document_id && (
                      <button onClick={() => downloadAuthed(`/documents/${g.declaration.document_id}/download`, `Gifted Deposit Declaration - ${g.name}.pdf`)}
                        className="text-[11px] text-[#100f5d] hover:underline" data-testid={`giftor-dl-declaration-${g.id}`}>Download declaration</button>
                    )}
                    {g.didit?.session_id && (
                      <button onClick={() => refreshId(g)} disabled={idBusy === `refresh-${g.id}`}
                        className="inline-flex items-center gap-1 text-[11px] text-[#100f5d] hover:underline disabled:opacity-50"
                        data-testid={`giftor-refresh-id-${g.id}`}>
                        <RefreshCw className={`w-3 h-3 ${idBusy === `refresh-${g.id}` ? "animate-spin" : ""}`} />
                        Refresh ID check
                      </button>
                    )}
                    {g.didit?.session_id && (
                      <button onClick={() => idReport(g)} disabled={idBusy === `report-${g.id}`}
                        className="inline-flex items-center gap-1 text-[11px] text-[#100f5d] hover:underline disabled:opacity-50"
                        data-testid={`giftor-id-report-${g.id}`}>
                        <FileText className="w-3 h-3" />
                        {g.didit?.report_doc_id ? "Download ID report" : (idBusy === `report-${g.id}` ? "Fetching…" : "Get ID report from Didit")}
                      </button>
                    )}
                    {g.didit?.session_id && !g.steps?.id_check && (
                      <span className="text-[11px] text-slate-400">ID status: {g.didit.status}{g.id_in_review ? " (in review — nothing needed from giftor)" : ""}</span>
                    )}
                  </div>
                )}
                {isSolicitor && (
                  <div className="mt-2" data-testid={`giftor-funds-list-${g.id}`}>
                    <p className="text-[11px] font-semibold text-slate-600 mb-1 flex items-center gap-1"><Banknote className="w-3 h-3" /> Proof of funds</p>
                    {(g.funds || []).length ? (
                      <div className="space-y-1">
                        {(g.funds || []).map((f) => (
                          <button key={f.id} onClick={() => downloadAuthed(`/documents/${f.id}/download`, f.filename).catch(() => toast.error("Could not download this file."))}
                            className="w-full flex items-center gap-2 rounded border border-slate-200 bg-white px-2.5 py-1.5 text-left hover:bg-slate-50"
                            data-testid={`giftor-dl-funds-${f.id}`}>
                            <FileText className="w-3.5 h-3.5 text-[#100f5d] shrink-0" />
                            <span className="text-[12px] text-slate-800 truncate flex-1">{f.filename}</span>
                            {f.size ? <span className="text-[10px] text-slate-400 shrink-0">{Math.max(1, Math.round(f.size / 1024))} KB</span> : null}
                            <span className="text-[10px] text-slate-400 shrink-0">{f.uploaded_at ? new Date(f.uploaded_at).toLocaleString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : ""}</span>
                          </button>
                        ))}
                      </div>
                    ) : <p className="text-[11px] text-slate-400">No proof of funds uploaded yet.</p>}
                  </div>
                )}
                {isSolicitor && g.didit?.session_id && !g.steps?.id_check && (
                  <div className="mt-2 flex flex-wrap items-center gap-2" data-testid={`giftor-id-review-${g.id}`}>
                    <span className="text-[11px] text-slate-500">Review ID check:</span>
                    <button onClick={() => decideId(g, "Approved")} disabled={idBusy === `decide-${g.id}`}
                      className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-md bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
                      data-testid={`giftor-approve-id-${g.id}`}>
                      <Check className="w-3 h-3" /> {idBusy === `decide-${g.id}` ? "Saving…" : "Approve check"}
                    </button>
                    <button onClick={() => decideId(g, "Declined")} disabled={idBusy === `decide-${g.id}`}
                      className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-md border border-rose-300 text-rose-600 hover:bg-rose-50 disabled:opacity-50"
                      data-testid={`giftor-decline-id-${g.id}`}>
                      <X className="w-3 h-3" /> Decline
                    </button>
                  </div>
                )}
                {isSolicitor && (
                  <div className="mt-2 flex flex-wrap items-center gap-2" data-testid={`giftor-manual-${g.id}`}>
                    <span className="text-[11px] text-slate-500">Mark off manually:</span>
                    {[["declaration", "Declaration"], ["funds", "Proof of funds"], ["id_check", "ID / AML"]].map(([step, label]) => {
                      const done = g.steps?.[step];
                      const overridden = g.overrides?.[step]?.done || g.overrides?.[step]?.status === "accepted";
                      const busyKey = `mark-${step}-${g.id}`;
                      const rejectKey = `reject-${step}-${g.id}`;
                      return (
                        <span key={step} className="inline-flex items-center gap-1">
                          {done && overridden ? (
                            <button onClick={() => markStep(g, step, false)} disabled={idBusy === busyKey}
                              className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 hover:bg-emerald-100 disabled:opacity-50"
                              title={`Marked by ${g.overrides?.[step]?.by || "solicitor"} — click to undo`}
                              data-testid={`giftor-unmark-${step}-${g.id}`}>
                              <CheckCircle2 className="w-3 h-3" /> {label} (manual) — undo
                            </button>
                          ) : !done ? (
                            <button onClick={() => markStep(g, step, true)} disabled={idBusy === busyKey}
                              className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full border border-slate-300 text-slate-600 hover:border-[#100f5d] hover:text-[#100f5d] disabled:opacity-50"
                              data-testid={`giftor-mark-${step}-${g.id}`}>
                              <Check className="w-3 h-3" /> {label}
                            </button>
                          ) : null}
                          <button onClick={() => rejectStep(g, step)} disabled={idBusy === rejectKey}
                            className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full border border-rose-200 text-rose-600 hover:bg-rose-50 disabled:opacity-50"
                            title={`Cancel ${label} and ask ${g.name} to resubmit`}
                            data-testid={`giftor-reject-${step}-${g.id}`}>
                            <X className="w-3 h-3" /> re-request {label}
                          </button>
                        </span>
                      );
                    })}
                  </div>
                )}
                {isSolicitor && (() => {
                  const ov = g.overrides || {};
                  const entries = Object.keys(GIFTOR_STEP_LABELS)
                    .map(step => ({ step, o: ov[step] }))
                    .filter(x => x.o && (x.o.status === "accepted" || x.o.status === "rejected"));
                  if (!entries.length) return null;
                  return (
                    <div className="mt-2 space-y-0.5" data-testid={`giftor-audit-list-${g.id}`}>
                      {entries.map(({ step, o }) => (
                        <p key={step} className="text-[10px] text-slate-400" data-testid={`giftor-audit-${step}-${g.id}`}>
                          <span className={o.status === "accepted" ? "font-semibold text-emerald-600" : "font-semibold text-rose-600"}>{GIFTOR_STEP_LABELS[step]}</span>{" "}
                          {_ovVerb(o.status)}{o.by ? ` by ${o.by}` : ""}{o.at ? ` · ${_ovDate(o.at)}` : ""}
                          {o.note ? <span className="italic"> — "{o.note}"</span> : null}
                        </p>
                      ))}
                    </div>
                  );
                })()}
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                <button onClick={() => resend(g)} className="text-slate-400 hover:text-[#100f5d]" title="Resend secure link" data-testid={`giftor-resend-${g.id}`}><Send className="w-4 h-4" /></button>
                <button onClick={() => remove(g)} className="text-slate-400 hover:text-rose-600" title="Remove giftor" data-testid={`giftor-remove-${g.id}`}><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {adding ? (
        <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50 p-3 space-y-2" data-testid="giftor-add-form">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} placeholder="First name" className="border border-slate-300 rounded-md px-3 py-2 text-sm" data-testid="giftor-first-name" />
            <input value={form.middle_name} onChange={(e) => setForm({ ...form, middle_name: e.target.value })} placeholder="Middle name(s)" className="border border-slate-300 rounded-md px-3 py-2 text-sm" data-testid="giftor-middle-name" />
            <input value={form.surname} onChange={(e) => setForm({ ...form, surname: e.target.value })} placeholder="Surname" className="border border-slate-300 rounded-md px-3 py-2 text-sm" data-testid="giftor-surname" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Giftor email" className="border border-slate-300 rounded-md px-3 py-2 text-sm" data-testid="giftor-email" />
            <select value={form.relationship} onChange={(e) => setForm({ ...form, relationship: e.target.value })} className="border border-slate-300 rounded-md px-3 py-2 text-sm bg-white" data-testid="giftor-relationship">
              <option value="">Relationship to buyer…</option>
              {REL_OPTIONS.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
            <input value={form.gift_amount} onChange={(e) => setForm({ ...form, gift_amount: e.target.value.replace(/[^0-9.]/g, "") })} placeholder="Gift amount (£)" inputMode="decimal" className="border border-slate-300 rounded-md px-3 py-2 text-sm" data-testid="giftor-amount" />
          </div>
          <div className="flex gap-2 justify-end">
            <button onClick={() => setAdding(false)} className="text-sm px-3 py-1.5 rounded-md border border-slate-300 text-slate-600">Cancel</button>
            <button onClick={submit} disabled={busy} className="text-sm px-3 py-1.5 rounded-md bg-[#100f5d] text-white hover:bg-[#1a1980] inline-flex items-center gap-1.5" data-testid="giftor-add-submit">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Add & send link
            </button>
          </div>
        </div>
      ) : (
        <button onClick={() => setAdding(true)} className="mt-3 inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-md border border-dashed border-slate-300 text-slate-600 hover:border-[#100f5d] hover:text-[#100f5d]" data-testid="giftor-add-btn">
          <Plus className="w-4 h-4" /> Add a giftor
        </button>
      )}
    </div>
  );
}
